import React from "react";
import PropTypes from "prop-types";
import {
  PredictionFeedback,
  ColorSelectQuestionContent,
} from "@learningblocks/storybook-components";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import { LogEvent } from "@learningblocks/shared-components";
import { QuestionWrapper } from "../Components/QuestionWrapper";
import { MainTitle } from "../Components/MainTitle";
import { Footer } from "../Components/Footer";
import { SubmitButton } from "../Components/SubmitButton";
import { ResetButton } from "../Components/ResetButton";
import { useIntl } from "react-intl";
import { TranslationsProvider } from "@learningblocks/simulations/shared-components";
import { fetchTranslations } from "../locales/config";

import "./style.scss";

const FEEDBACK = {
  SUCCESS: "SUCCESS",
  FAIL: "FAIL",
};
/**
 * ColorPrediction
 *  - Question Component to select a color for each solution
 */
export function ColorPredictionContent(props) {
  const intl = useIntl();
  const getFeedbackProps = () => {
    const successMessage = "Prediction Submitted!";
    const failureMessage = "Select a color for all options.";
    if (props.feedbackType === FEEDBACK.SUCCESS) {
      return {
        type: FEEDBACK.SUCCESS,
        message: intl.formatMessage({
          id: successMessage,
          defaultMessage: successMessage,
        }),
        className: "colorPredictionQuestion__feedback",
      };
    } else if (props.feedbackType === FEEDBACK.FAIL) {
      return {
        type: FEEDBACK.FAIL,
        message: intl.formatMessage({
          id: failureMessage,
          defaultMessage: failureMessage,
        }),
        className: "colorPredictionQuestion__feedback",
      };
    } else {
      return { type: "" };
    }
  };

  const onColorSelect = (params) => {
    props.onColorSelect({
      componentID: props.id,
      optionID: params.optionIndex,
      value: params.value,
    });
  };

  const onSubmit = () => {
    props.onSubmit({
      componentID: props.id,
      count: props.options.length,
    });
  };

  const onReset = () => {
    props.onReset({
      componentID: props.id,
    });
  };

  const wrapperClass = `colorPredictionQuestion ${props.className}`;

  return (
    <QuestionWrapper className={wrapperClass}>
      <MainTitle
        title={intl.formatMessage({
          id: "Prediction Question",
          defaultMessage: "Prediction Question",
          description: "",
        })}
        className="colorPredictionQuestion__mainTitle"
      />
      <p>
        <RichText value={props.text} />
      </p>
      <ColorSelectQuestionContent
        question={props.question}
        options={props.options}
        onColorSelect={onColorSelect}
      />
      <PredictionFeedback {...getFeedbackProps()} />
      <Footer className="colorPredictionQuestion__footer">
        <ResetButton
          disabled={!props.submitted}
          onReset={onReset}
          className="colorPredictionQuestion__footer__reset"
        />
        <LogEvent
          logEventProps={{
            event_type: "sim_prediction_question clicked",
            event_schema: "track",
          }}
          actionProps={{ onSubmit: { action: "click" } }}
          elementType="button"
        >
          <SubmitButton
            disabled={props.submitted}
            onSubmit={onSubmit}
            label={intl.formatMessage({
              id: "Submit prediction",
              defaultMessage: "Submit prediction",
              description: "",
            })}
            className="colorPredictionQuestion__footer__submit"
          />
        </LogEvent>
      </Footer>
    </QuestionWrapper>
  );
}

export function ColorPrediction(props) {
  return (
    <TranslationsProvider fetchTranslations={fetchTranslations}>
      <ColorPredictionContent {...props} />
    </TranslationsProvider>
  );
}

ColorPrediction.propTypes = {
  /**
   * Question text
   */
  question: PropTypes.string,
  /**
   * Options for the question
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Label for option
       */
      optionLabel: PropTypes.string.isRequired,
      /**
       * Data needed to render optionTube
       */
      optionTube: PropTypes.shape({
        /**
         * Type of the tube Image
         */
        type: PropTypes.oneOf(["TUBE", "GLASS_TUBE"]).isRequired,
      }),
      /**
       * List of Checkboxes to display for option
       */
      checkboxes: PropTypes.arrayOf(PropTypes.object).isRequired,
      /**
       * data to show feedback
       */
      feedback: PropTypes.shape({
        /**
         * Boolean to set if feedback should be displayed
         */
        showFeedback: PropTypes.bool.isRequired,
        /**
         * Boolean to set if feedback should be correct/incorrect state
         */
        isCorrect: PropTypes.bool,
        /**
         * Message to display on feedback
         */
        message: PropTypes.string,
      }).isRequired,
    })
  ),
  /**
   * Type of feedback to show
   */
  feedbackType: PropTypes.oneOf(["SUCCESS", "FAIL", ""]),
  /**
   * Function to execute when color selected in checkbox
   */
  onColorSelect: PropTypes.func,
  /**
   * Funtion to execute on click Reset Button
   */
  onReset: PropTypes.func,
  /**
   * Functon to execute on click Submit Button
   */
  onSubmit: PropTypes.func,
  /**
   * className to be set for ColorSelection
   */
  className: PropTypes.string,
};

ColorPrediction.defaultProps = {
  feedbackType: "",
  className: "",
};
